import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from "react";
import {
  ApiError,
  api,
  registerAuthClearedHandler,
  setAccessToken,
} from "@/lib/api";

export type Plan = "trial" | "starter" | "pro" | "past_due" | "expired";
export type Role = "owner" | "admin" | "member";

export interface User {
  _id: string;
  email: string;
  full_name: string;
  role: Role;
  email_verified: boolean;
  onboarding_completed: boolean;
  workspace_id: string;
  created_at: string;
}

export interface Workspace {
  _id: string;
  name: string;
  plan: Plan;
  trial_ends_at?: string | null;
  owner_user_id: string;
  logo_url?: string | null;
  billing?: {
    plan?: string | null;
    status?: string | null;
    current_period_end?: string | null;
    cancel_at_period_end?: boolean;
    card_brand?: string | null;
    card_last4?: string | null;
  } | null;
  created_at: string;
}

interface AuthResponse {
  access_token: string;
  user: User;
  workspace: Workspace;
}

interface AuthState {
  user: User | null;
  workspace: Workspace | null;
  status: "loading" | "authenticated" | "unauthenticated";
}

interface AuthContextValue extends AuthState {
  signup: (data: {
    email: string;
    password: string;
    full_name: string;
    plan?: "starter" | "pro";
  }) => Promise<void>;
  signupWithInvite: (data: {
    token: string;
    full_name: string;
    password: string;
  }) => Promise<void>;
  login: (data: { email: string; password: string }) => Promise<void>;
  logout: () => Promise<void>;
  refreshMe: () => Promise<void>;
  setUserPartial: (patch: Partial<User>) => void;
  setWorkspacePartial: (patch: Partial<Workspace>) => void;
  resendVerification: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    workspace: null,
    status: "loading",
  });
  const bootedRef = useRef(false);

  const applyAuth = useCallback((resp: AuthResponse) => {
    setAccessToken(resp.access_token);
    setState({
      user: resp.user,
      workspace: resp.workspace,
      status: "authenticated",
    });
  }, []);

  const clearAuth = useCallback(() => {
    setAccessToken(null);
    setState({ user: null, workspace: null, status: "unauthenticated" });
  }, []);

  // Wire up the api client → context unauth callback
  useEffect(() => {
    registerAuthClearedHandler(() => {
      setAccessToken(null);
      setState({ user: null, workspace: null, status: "unauthenticated" });
    });
  }, []);

  // Silent refresh on boot
  useEffect(() => {
    if (bootedRef.current) return;
    bootedRef.current = true;
    (async () => {
      try {
        const refreshed = await api.post<{ access_token: string }>(
          "/auth/refresh",
          undefined,
          { auth: false }
        );
        setAccessToken(refreshed.access_token);
        const me = await api.get<AuthResponse>("/auth/me");
        applyAuth(me);
      } catch {
        clearAuth();
      }
    })();
  }, [applyAuth, clearAuth]);

  const signup = useCallback<AuthContextValue["signup"]>(
    async (data) => {
      const resp = await api.post<AuthResponse>("/auth/signup", data, {
        auth: false,
      });
      applyAuth(resp);
    },
    [applyAuth]
  );

  const signupWithInvite = useCallback<AuthContextValue["signupWithInvite"]>(
    async (data) => {
      const resp = await api.post<AuthResponse>(
        "/auth/signup-with-invite",
        data,
        { auth: false },
      );
      applyAuth(resp);
    },
    [applyAuth]
  );

  const login = useCallback<AuthContextValue["login"]>(
    async (data) => {
      const resp = await api.post<AuthResponse>("/auth/login", data, {
        auth: false,
      });
      applyAuth(resp);
    },
    [applyAuth]
  );

  const logout = useCallback<AuthContextValue["logout"]>(async () => {
    try {
      await api.post("/auth/logout", undefined, { auth: false });
    } catch {
      /* ignore */
    }
    clearAuth();
  }, [clearAuth]);

  const refreshMe = useCallback<AuthContextValue["refreshMe"]>(async () => {
    const me = await api.get<AuthResponse>("/auth/me");
    applyAuth(me);
  }, [applyAuth]);

  const setUserPartial = useCallback((patch: Partial<User>) => {
    setState((s) => (s.user ? { ...s, user: { ...s.user, ...patch } } : s));
  }, []);

  const setWorkspacePartial = useCallback((patch: Partial<Workspace>) => {
    setState((s) =>
      s.workspace ? { ...s, workspace: { ...s.workspace, ...patch } } : s
    );
  }, []);

  const resendVerification = useCallback(async () => {
    try {
      await api.post("/auth/resend-verification");
    } catch (e) {
      if (!(e instanceof ApiError)) throw e;
      throw e;
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      signup,
      signupWithInvite,
      login,
      logout,
      refreshMe,
      setUserPartial,
      setWorkspacePartial,
      resendVerification,
    }),
    [
      state,
      signup,
      signupWithInvite,
      login,
      logout,
      refreshMe,
      setUserPartial,
      setWorkspacePartial,
      resendVerification,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
